import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Dress, Clock, Star, Phone, WhatsappLogo, MapPin, CalendarBlank, Car, ShieldCheck } from "@phosphor-icons/react";
import { Helmet } from "react-helmet-async";

const whatsappMsg = encodeURIComponent("Bonjour, je souhaite organiser mon transport pour la Fashion Week Paris avec ALTURA. Pouvez-vous me faire un devis ?");
const whatsappUrl = `https://wa.me/33681969342?text=${whatsappMsg}`;

const FashionWeekPage = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Transport Privé Fashion Week Paris — ALTURA",
    "description": "Service de chauffeur privé pour la Paris Fashion Week. Transport entre défilés, showrooms et soirées. Ponctualité et discrétion garanties.",
    "url": "https://alturaconciergerie.com/experiences/fashion-week",
    "provider": {
      "@type": "LocalBusiness",
      "name": "ALTURA Conciergerie",
      "telephone": "+33681969342",
      "address": { "@type": "PostalAddress", "addressLocality": "Paris", "addressCountry": "FR" },
    },
  };

  return (
    <main className="pt-20 bg-[#EDEBE4]" data-testid="fashion-week-page">
      <Helmet>
        <title>Chauffeur Privé Fashion Week Paris | Transport Défilés | ALTURA</title>
        <meta name="description" content="Transport privé pour la Paris Fashion Week. Chauffeur dédié entre les défilés, showrooms et after-parties. Mercedes Classe S, discrétion et ponctualité." />
        <link rel="canonical" href="https://alturaconciergerie.com/experiences/fashion-week" />
        <meta property="og:title" content="Chauffeur Privé Fashion Week Paris | ALTURA" />
        <meta property="og:description" content="Naviguez entre les défilés et showrooms avec élégance. Service de transport privé dédié à la Fashion Week." />
        <meta property="og:url" content="https://alturaconciergerie.com/experiences/fashion-week" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://images.unsplash.com/photo-1732210571843-6789f9090b0e?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="relative h-[60vh] min-h-[450px] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1732210571843-6789f9090b0e?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85"
          alt="Fashion Week Paris — transport privé ALTURA"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#EDEBE4] via-[#EDEBE4]/40 to-[#1A1A1A]/30" />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-5xl mx-auto px-6 sm:px-12 pb-12 w-full">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
              <p className="text-xs tracking-[0.2em] uppercase text-[#1A1A1A]/70 mb-3">ALTURA · Expériences</p>
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-normal text-[#1A1A1A] leading-tight">
                Fashion Week Paris
              </h1>
              <p className="text-[#1A1A1A]/70 text-base mt-4 max-w-xl font-light">
                Votre chauffeur privé au cœur de la mode. Ponctualité, discrétion, élégance.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 sm:py-20">
        <div className="max-w-5xl mx-auto px-6 sm:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <h2 className="font-heading text-2xl sm:text-3xl font-normal text-[#1A1A1A] mb-6">L'élégance au service de la mode</h2>
              <p className="text-[#1A1A1A]/80 font-light leading-relaxed mb-6">
                La Paris Fashion Week est un marathon. Entre les défilés au Palais de Tokyo, les showrooms du Marais, les rendez-vous presse à Saint-Germain et les soirées dans le VIIIe, chaque minute compte. ALTURA vous offre un service de chauffeur privé pensé pour le rythme intense de la mode.
              </p>
              <p className="text-[#1A1A1A]/80 font-light leading-relaxed mb-8">
                Votre chauffeur connaît les lieux des défilés, les accès VIP, les rues à éviter et les raccourcis. Il s'adapte en temps réel aux changements de programme — retards, invitations de dernière minute, after-parties. Vous arrivez toujours à l'heure, impeccable.
              </p>
              <div className="flex flex-wrap gap-4">
                <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="bg-[#25D366] text-white px-8 py-3 text-sm tracking-wider uppercase hover:bg-[#20bd5a] transition-colors inline-flex items-center gap-2" data-testid="fashion-cta-whatsapp">
                  <WhatsappLogo size={18} weight="fill" /> Devis Fashion Week
                </a>
                <Link to="/contact" className="border border-[#1A1A1A] text-[#1A1A1A] px-8 py-3 text-sm tracking-wider uppercase hover:bg-[#1A1A1A] hover:text-[#EDEBE4] transition-colors inline-flex items-center gap-2" data-testid="fashion-cta-contact">
                  <Phone size={16} /> Nous contacter
                </Link>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.2 }}>
              <img
                src="https://images.unsplash.com/photo-1732210571866-263232bff128?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85"
                alt="Style parisien — Fashion Week ALTURA"
                className="w-full h-[400px] object-cover object-top"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 sm:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
            <p className="text-xs tracking-[0.2em] uppercase text-[#1A1A1A]/60 mb-4">Nos prestations</p>
            <h2 className="font-heading text-2xl sm:text-3xl font-normal text-[#1A1A1A]">Un service taillé pour la Fashion Week</h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: <Car size={28} weight="thin" />, title: "Mise à disposition", desc: "Chauffeur et véhicule dédiés pendant toute la durée de la Fashion Week. Disponibilité totale, de tôt le matin jusqu'à tard dans la nuit." },
              { icon: <Clock size={28} weight="thin" />, title: "Ponctualité absolue", desc: "Les défilés n'attendent pas. Votre chauffeur anticipe les trajets, connaît les accès et garantit votre arrivée à l'heure — chaque fois." },
              { icon: <ShieldCheck size={28} weight="thin" />, title: "Discrétion garantie", desc: "Confidentialité totale pour les créateurs, mannequins, acheteurs et journalistes. Vitres teintées, itinéraires discrets." },
              { icon: <MapPin size={28} weight="thin" />, title: "Connaissance des lieux", desc: "Palais de Tokyo, Grand Palais, Tuileries, Carreau du Temple, showrooms privés — votre chauffeur connaît chaque adresse." },
              { icon: <CalendarBlank size={28} weight="thin" />, title: "Planning flexible", desc: "Programme qui change en dernière minute ? Invitation surprise ? Votre chauffeur s'adapte instantanément à vos besoins." },
              { icon: <Star size={28} weight="thin" />, title: "Flotte premium", desc: "Mercedes Classe S pour l'élégance, Classe V pour les équipes, Range Rover pour l'impact. Choisissez le véhicule qui correspond à votre image." },
            ].map((service, i) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="bg-[#EDEBE4] border border-[#1A1A1A]/10 p-6"
              >
                <div className="text-[#1A1A1A] mb-4">{service.icon}</div>
                <h3 className="font-heading text-lg text-[#1A1A1A] mb-2">{service.title}</h3>
                <p className="text-[#1A1A1A]/70 text-sm font-light leading-relaxed">{service.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Calendrier Fashion Week */}
      <section className="py-16 sm:py-20 bg-[#EDEBE4]">
        <div className="max-w-5xl mx-auto px-6 sm:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="font-heading text-2xl sm:text-3xl font-normal text-[#1A1A1A] mb-4">Paris, capitale mondiale de la mode</h2>
            <p className="text-[#1A1A1A]/80 font-light leading-relaxed mb-8 max-w-3xl">
              La Paris Fashion Week se déroule quatre fois par an : les collections femme en janvier et septembre, les collections homme en janvier et juin, et la Haute Couture en janvier et juillet. Chaque édition attire des milliers de professionnels du monde entier.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {[
                { period: "Janvier", event: "Haute Couture Printemps-Été & Menswear Automne-Hiver", desc: "Ouverture de l'année mode avec les collections les plus prestigieuses." },
                { period: "Février-Mars", event: "Prêt-à-porter Automne-Hiver Femme", desc: "La semaine la plus intense : plus de 100 défilés en 10 jours." },
                { period: "Juin", event: "Menswear Printemps-Été", desc: "Les collections homme dans une ambiance estivale." },
                { period: "Septembre-Octobre", event: "Prêt-à-porter Printemps-Été Femme", desc: "Le rendez-vous incontournable de la rentrée mode." },
              ].map((item, i) => (
                <motion.div
                  key={item.period}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-white border border-[#1A1A1A]/10 p-6"
                >
                  <p className="text-xs tracking-[0.2em] uppercase text-[#1A1A1A]/60 mb-2">{item.period}</p>
                  <h3 className="font-heading text-lg text-[#1A1A1A] mb-2">{item.event}</h3>
                  <p className="text-[#1A1A1A]/70 text-sm font-light">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 sm:px-12">
          <h2 className="font-heading text-2xl sm:text-3xl font-normal text-[#1A1A1A] mb-10">Questions fréquentes</h2>
          <div className="space-y-6">
            {[
              { q: "Combien coûte un chauffeur pour la Fashion Week ?", a: "Nous proposons des forfaits de mise à disposition adaptés à la durée de votre séjour : journée, semaine complète ou sur mesure. Contactez-nous pour un devis personnalisé selon vos besoins." },
              { q: "Peut-on réserver pour toute la durée de la Fashion Week ?", a: "Absolument. Nous recommandons de réserver le plus tôt possible car la demande est très forte pendant les semaines de mode. Un même chauffeur et véhicule vous sont dédiés pour toute la période." },
              { q: "Le chauffeur connaît-il les lieux des défilés ?", a: "Oui. Nos chauffeurs sont briefés sur les lieux officiels (Palais de Tokyo, Grand Palais, Tuileries, Carreau du Temple) et les showrooms privés. Ils connaissent les accès VIP et les zones de dépose." },
              { q: "Proposez-vous un service de nuit ?", a: "Oui, nos chauffeurs sont disponibles 24h/24. Nous assurons le transport vers et depuis les after-parties et soirées privées, quelle que soit l'heure." },
              { q: "Puis-je réserver plusieurs véhicules pour mon équipe ?", a: "Bien sûr. Nous pouvons mobiliser plusieurs véhicules avec chauffeur pour transporter votre équipe (stylistes, mannequins, photographes, attachés de presse)." },
            ].map((item, i) => (
              <motion.div key={item.q} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                <h3 className="text-[#1A1A1A] font-medium text-sm mb-2">{item.q}</h3>
                <p className="text-[#1A1A1A]/70 text-sm font-light leading-relaxed">{item.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-[#1A1A1A]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl text-[#EDEBE4] mb-4">Prêt pour la Fashion Week ?</h2>
          <p className="text-[#EDEBE4]/60 text-sm mb-8">Réservez votre chauffeur dédié et vivez la mode parisienne sans contrainte.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="bg-[#25D366] text-white px-10 py-4 text-sm tracking-wider uppercase hover:bg-[#20bd5a] transition-colors inline-flex items-center gap-2" data-testid="fashion-cta-bottom-wa">
              <WhatsappLogo size={18} weight="fill" /> Devis WhatsApp
            </a>
            <a href="tel:+33681969342" className="bg-[#EDEBE4] text-[#1A1A1A] px-10 py-4 text-sm tracking-wider uppercase hover:bg-white transition-colors inline-flex items-center gap-2" data-testid="fashion-cta-bottom-call">
              <Phone size={16} /> +33 6 81 96 93 42
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default FashionWeekPage;
